import axios from "axios";
import { useCallback, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { DCDesignCollection } from "./types";

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const collectionFields = "id brand_id name is_artist_based is_global square_logo_url rectangular_logo_url header_image_url header_image_filepath header_rip_status created_at updated_at";

type UploadUrl = {
  uploadUrl: string;
  publicUrl: string;
  path: string;
};

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, {
    headers: { "X-Wallmates-Auth": authToken }
  });
  if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
  return data.data;
};

export const useDesignCollections = () => {
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();
  const [collections, setCollections] = useState<DCDesignCollection[]>([]);
  const [globalCollection, setGlobalCollection] = useState<DCDesignCollection | null>(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    try {
      setLoading(true);
      return await fn();
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Request failed" });
      return null;
    } finally {
      setLoading(false);
    }
  }, [loginFunctions.logout, setSnackbar]);

  const listCollections = useCallback((brandId: number) => run(async () => {
    const q = `query($brand_id:ID!){ dcDesignCollections(brand_id:$brand_id){ ${collectionFields} } }`;
    const data = await graphqlRequest(authToken, q, { brand_id: brandId });
    const next = (data?.dcDesignCollections || []) as DCDesignCollection[];
    setCollections(next);
    return next;
  }), [authToken, run]);

  const fetchGlobalCollection = useCallback((brandId: number) => run(async () => {
    const q = `query($brand_id:ID!){ dcDesignGlobalCollection(brand_id:$brand_id){ ${collectionFields} } }`;
    const data = await graphqlRequest(authToken, q, { brand_id: brandId });
    const next = (data?.dcDesignGlobalCollection || null) as DCDesignCollection | null;
    setGlobalCollection(next);
    return next;
  }), [authToken, run]);

  const createCollectionUpload = useCallback((file: File) => run(async () => {
    const q = `mutation($fileName:String!,$contentType:String){ createDCDesignCollectionUpload(fileName:$fileName,contentType:$contentType){ uploadUrl publicUrl path } }`;
    const data = await graphqlRequest(authToken, q, { fileName: file.name, contentType: file.type || "application/octet-stream" });
    return data?.createDCDesignCollectionUpload as UploadUrl;
  }), [authToken, run]);

  const uploadCollectionImage = useCallback(async (file: File): Promise<string | null> => {
    try {
      const upload = await createCollectionUpload(file);
      if (!upload?.uploadUrl || !upload?.publicUrl) {
        throw new Error("Failed to create upload URL.");
      }
      await axios.put(upload.uploadUrl, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" }
      });
      return upload.publicUrl;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to upload collection image." });
      return null;
    }
  }, [createCollectionUpload, loginFunctions.logout, setSnackbar]);

  const createCollection = useCallback((input: Partial<DCDesignCollection>) => run(async () => {
    const q = `mutation($input:DCDesignCollectionInput!){ createDCDesignCollection(input:$input){ ${collectionFields} } }`;
    const data = await graphqlRequest(authToken, q, { input });
    const created = data?.createDCDesignCollection as DCDesignCollection;
    setCollections((prev) => [...prev.filter((item) => item.id !== created.id), created]);
    if (created?.is_global) setGlobalCollection(created);
    return created;
  }), [authToken, run]);

  const updateCollection = useCallback((id: number, input: Partial<DCDesignCollection>) => run(async () => {
    const q = `mutation($id:ID!,$input:DCDesignCollectionInput!){ updateDCDesignCollection(id:$id,input:$input){ ${collectionFields} } }`;
    const data = await graphqlRequest(authToken, q, { id, input });
    const updated = data?.updateDCDesignCollection as DCDesignCollection;
    setCollections((prev) => {
      return prev.map((item) => {
        if (item.id === updated.id) return updated;
        if (updated?.is_global && item.is_global) {
          return { ...item, is_global: false };
        }
        return item;
      });
    });
    if (updated?.is_global) setGlobalCollection(updated);
    return updated;
  }), [authToken, run]);

  // Collection headers do NOT go through the Supabase logo upload path — they must ride the
  // same S3 headers bucket + HEADER_RIP pipeline as the brand static header, which is why
  // this posts multipart to the brands service instead of GraphQL.
  const uploadCollectionHeader = useCallback(async (collectionId: number, file: File): Promise<{ header_image: string } | null> => {
    try {
      setLoading(true);
      const payload = new FormData();
      payload.append("static_header", file);
      const { data } = await axios.put(
        `${environment.brandsUrl}/collections/${collectionId}/header`,
        payload,
        { headers: { "Content-Type": "multipart/form-data", "X-Wallmates-Auth": authToken } }
      );
      setCollections((prev) => prev.map((item) => (
        item.id === collectionId
          // `header_image` is a presigned GET url — good enough for an immediate preview.
          // The stored column holds the S3 key; the next list refresh replaces this value.
          ? { ...item, header_image_url: data?.header_image || item.header_image_url, header_image_filepath: null, header_rip_status: "unprocessed" as const }
          : item
      )));
      setSnackbar({ show: true, snackbarLevel: "info", text: "Collection header uploaded. It will be ready to print once processing finishes." });
      return data as { header_image: string };
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.response?.data?.message || "Failed to upload collection header." });
      return null;
    } finally {
      setLoading(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const clearCollectionHeader = useCallback(async (collectionId: number): Promise<boolean> => {
    try {
      setLoading(true);
      await axios.delete(`${environment.brandsUrl}/collections/${collectionId}/header`, {
        headers: { "X-Wallmates-Auth": authToken }
      });
      setCollections((prev) => prev.map((item) => (
        item.id === collectionId
          ? { ...item, header_image_url: null, header_image_filepath: null, header_rip_status: null }
          : item
      )));
      setSnackbar({ show: true, snackbarLevel: "info", text: "Collection header removed. This collection now uses the brand header." });
      return true;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.response?.data?.message || "Failed to remove collection header." });
      return false;
    } finally {
      setLoading(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const countDesignsByCollection = useCallback((collectionId: number) => run(async () => {
    const q = `query($collection_id:ID!){ dcDesignCountByCollection(collection_id:$collection_id) }`;
    const data = await graphqlRequest(authToken, q, { collection_id: collectionId });
    return (data?.dcDesignCountByCollection ?? 0) as number;
  }), [authToken, run]);

  const deleteCollection = useCallback((id: number) => run(async () => {
    const q = `mutation($id:ID!){ deleteDCDesignCollection(id:$id){ id } }`;
    await graphqlRequest(authToken, q, { id });
    setCollections((prev) => prev.filter((item) => item.id !== id));
    if (globalCollection?.id === id) setGlobalCollection(null);
    return true;
  }), [authToken, run, globalCollection?.id]);

  return {
    collections,
    globalCollection,
    loading,
    listCollections,
    fetchGlobalCollection,
    createCollectionUpload,
    uploadCollectionImage,
    createCollection,
    updateCollection,
    uploadCollectionHeader,
    clearCollectionHeader,
    countDesignsByCollection,
    deleteCollection
  };
};
